import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Container,
  Paper,
  useTheme,
  Divider
} from '@mui/material';
import {
  AccountCircle as AccountCircleIcon,
  SwapHoriz as SwapIcon,
  Login as LoginIcon,
  MusicNote as MusicNoteIcon,
  Create as CreateIcon
} from '@mui/icons-material';
import { useAuthProvider } from '../../utils/auth';
import { AppRoutes } from '../../constants/routes';

const AccountTypeSwitcher = ({ 
  currentUserType,
  targetUserType = 'listener',
  title = "Account Type Mismatch",
  message = "You're currently signed in with the wrong account type for this feature."
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { deauthorizeUser } = useAuthProvider();

  const handleSignOut = async () => {
    await deauthorizeUser();
    navigate(AppRoutes.LoginPage);
  };

  const handleGoToLogin = () => {
    navigate(AppRoutes.LoginPage);
  };

  const getAccountTypeInfo = (userType) => {
    switch (userType) {
      case 'creator':
        return {
          icon: <CreateIcon sx={{ fontSize: 48, color: theme.palette.primary.main }} />,
          title: 'Creator Account',
          description: 'Upload and manage music, artists, and earnings',
          color: theme.palette.primary.main
        };
      case 'listener':
        return {
          icon: <MusicNoteIcon sx={{ fontSize: 48, color: theme.palette.secondary.main }} />,
          title: 'Listener Account',
          description: 'Stream music, create playlists, and discover new artists',
          color: theme.palette.secondary.main
        };
      default:
        return {
          icon: <AccountCircleIcon sx={{ fontSize: 48, color: theme.palette.text.secondary }} />,
          title: 'User Account',
          description: 'General user account',
          color: theme.palette.text.secondary
        };
    }
  };

  const currentInfo = getAccountTypeInfo(currentUserType);
  const targetInfo = getAccountTypeInfo(targetUserType);

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Paper 
        elevation={3}
        sx={{ 
          p: 4, 
          textAlign: 'center',
          backgroundColor: theme.palette.background.paper,
          borderRadius: 2
        }}
      >
        <Typography variant="h4" component="h1" gutterBottom>
          {title}
        </Typography>
        
        <Typography variant="body1" color="text.secondary" paragraph>
          {message}
        </Typography>

        <Box sx={{ mt: 4, mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            You're currently signed in as:
          </Typography>
          
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            gap: 2,
            mb: 2,
            p: 2,
            border: `2px solid ${currentInfo.color}`,
            borderRadius: 2,
            backgroundColor: `${currentInfo.color}10`
          }}>
            {currentInfo.icon}
            <Box sx={{ textAlign: 'left' }}>
              <Typography variant="h6" color={currentInfo.color}>
                {currentInfo.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {currentInfo.description}
              </Typography>
            </Box>
          </Box>

          <SwapIcon sx={{ fontSize: 32, color: theme.palette.text.secondary, my: 2 }} />

          <Typography variant="h6" gutterBottom>
            But you need a:
          </Typography>
          
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            gap: 2,
            p: 2,
            border: `2px solid ${targetInfo.color}`,
            borderRadius: 2,
            backgroundColor: `${targetInfo.color}10`
          }}>
            {targetInfo.icon}
            <Box sx={{ textAlign: 'left' }}>
              <Typography variant="h6" color={targetInfo.color}>
                {targetInfo.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {targetInfo.description}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" gutterBottom>
          What would you like to do?
        </Typography>

        <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            startIcon={<LoginIcon />}
            onClick={handleSignOut}
            sx={{ minWidth: 200 }}
          >
            Sign Out & Sign In as {targetInfo.title}
          </Button>
          
          <Button
            variant="outlined"
            startIcon={<LoginIcon />}
            onClick={handleGoToLogin}
            sx={{ minWidth: 200 }}
          >
            Go to Login Page
          </Button>
        </Box>

        <Box sx={{ mt: 3 }}>
          <Typography variant="body2" color="text.secondary">
            Don't have a {targetInfo.title.toLowerCase()} account? You can create one during sign-up.
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default AccountTypeSwitcher;
